import type { SupplierMatch, SupplierMatchStatus, SupplierSummary } from "@procuremate/shared";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { FormError } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { adminErrorMessage } from "./admin-error";
import { Card } from "./admin-layout";
import { SUPPLIER_MATCHES_PAGE_SIZE, useResolveSupplierMatch, useSupplierMatches } from "./api";

const VIEWS: SupplierMatchStatus[] = ["OPEN", "SAME", "DIFFERENT"];

/**
 * Admin → Suppliers (WP-3.3, ADR-0026 §5): pairs of suppliers that might be one company, found by
 * imports. A person decides: the same company (the newer is merged into the older) or different.
 * Order counts and dates only — never amounts.
 */
export function SupplierQueuePage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const status = (VIEWS as string[]).includes(params.get("status") ?? "")
    ? (params.get("status") as SupplierMatchStatus)
    : "OPEN";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const matches = useSupplierMatches({ status, page });
  const total = matches.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / SUPPLIER_MATCHES_PAGE_SIZE));

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  }

  return (
    <div className="flex flex-col gap-6">
      <Card title={t("admin.suppliers.title")} headingLevel={1}>
        <p className="max-w-prose text-sm text-fg-muted">{t("admin.suppliers.intro")}</p>
        <nav aria-label={t("admin.suppliers.views")} className="mt-4 flex flex-wrap gap-2">
          {VIEWS.map((view) => (
            <Link
              key={view}
              to={view === "OPEN" ? "/admin/suppliers" : `/admin/suppliers?status=${view}`}
              aria-current={view === status ? "page" : undefined}
              className={clsx(
                "rounded-full border px-3 py-1 text-sm font-semibold",
                view === status
                  ? "border-primary bg-primary text-on-primary"
                  : "border-border text-fg-muted hover:text-primary",
              )}
            >
              {t(`admin.suppliers.view.${view}`)}
            </Link>
          ))}
        </nav>
      </Card>

      {matches.isError ? (
        <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("auth.genericError")}
        </p>
      ) : null}
      {matches.data && matches.data.items.length === 0 ? (
        <p className="rounded-lg bg-surface p-6 text-center text-sm text-fg-muted shadow-card">
          {t(`admin.suppliers.empty.${status}`)}
        </p>
      ) : null}
      {matches.data?.items.map((match) => (
        <MatchCard key={match.id} match={match} />
      ))}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-fg-muted">
        <p aria-live="polite">{t("admin.suppliers.count", { count: total })}</p>
        {pages > 1 ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
              {t("admin.users.previous")}
            </Button>
            <span>{t("admin.users.pageOf", { page, pages })}</span>
            <Button variant="secondary" disabled={page >= pages} onClick={() => update({ page: String(page + 1) })}>
              {t("admin.users.next")}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MatchCard({ match }: { match: SupplierMatch }) {
  const { t, i18n } = useTranslation();
  const resolve = useResolveSupplierMatch();
  const [confirming, setConfirming] = useState<"same" | "different" | null>(null);
  const question = useRef<HTMLParagraphElement>(null);
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });

  useEffect(() => {
    if (confirming) question.current?.focus();
  }, [confirming]);

  const titleId = `match-${match.id}`;
  return (
    <section aria-labelledby={titleId} className="rounded-lg bg-surface p-5 shadow-card sm:p-6">
      <h2 id={titleId} className="text-lg font-bold text-primary">
        {t(`admin.suppliers.reason.${match.reason}`)}
      </h2>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SupplierBox label={t("admin.suppliers.kept")} supplier={match.kept} />
        <SupplierBox label={t("admin.suppliers.other")} supplier={match.other} />
      </div>

      {match.status === "OPEN" ? (
        <>
          {!confirming ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button onClick={() => setConfirming("same")}>{t("admin.suppliers.same")}</Button>
              <Button variant="secondary" onClick={() => setConfirming("different")}>
                {t("admin.suppliers.different")}
              </Button>
            </div>
          ) : (
            <div className="mt-4 rounded-md bg-bg p-4">
              {/* Focused on open: the question is read out, and Tab reaches the two choices next. */}
              <p ref={question} tabIndex={-1} className="text-sm text-fg outline-none">
                {confirming === "same"
                  ? t("admin.suppliers.confirmSame", { other: match.other.displayName, kept: match.kept.displayName })
                  : t("admin.suppliers.confirmDifferent")}
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                <Button
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate({ id: match.id, decision: confirming })}
                >
                  {confirming === "same" ? t("admin.suppliers.sameYes") : t("admin.suppliers.differentYes")}
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(null)}>
                  {t("admin.cancel")}
                </Button>
              </div>
            </div>
          )}
          <FormError message={adminErrorMessage(resolve.error, t, "admin.suppliers.alreadyDecided")} />
        </>
      ) : (
        <p className="mt-4 text-sm text-fg-muted">
          {t(`admin.suppliers.decided.${match.status}`, {
            name: match.resolvedBy ?? "—",
            date: match.resolvedAt ? dateFormat.format(new Date(match.resolvedAt)) : "—",
          })}
        </p>
      )}
    </section>
  );
}

function SupplierBox({ label, supplier }: { label: string; supplier: SupplierSummary }) {
  const { t, i18n } = useTranslation();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });
  return (
    <div className="min-w-0 rounded-md border border-border p-4">
      <p className="text-xs font-semibold tracking-wide text-fg-muted uppercase">{label}</p>
      <p className="mt-1 font-semibold break-words text-fg">{supplier.displayName}</p>
      <p className="mt-1 text-sm text-fg-muted">
        {supplier.lastOrderDate
          ? t("admin.suppliers.orders", {
              count: supplier.orderCount,
              date: dateFormat.format(new Date(supplier.lastOrderDate)),
            })
          : t("admin.suppliers.noOrders")}
      </p>
      {supplier.spellings.length > 1 ? (
        <>
          <p className="mt-2 text-sm text-fg-muted">{t("admin.suppliers.spellings")}</p>
          <ul className="list-disc pl-5 text-sm break-words text-fg">
            {supplier.spellings.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
